import {useResizeObserver} from '@github-ui/use-resize-observer'
import {type RefCallback, type RefObject, useCallback, useLayoutEffect, useRef, useState} from 'react'

/** One axis of the board, in pixels, as the board's own stylesheet resolved it. */
export type BoardAxisMetrics = {
  /**
   * One item's extent along this axis: a column's width, a row's height. Every item on
   * an axis is the same, because both extents are stated once for the whole board.
   */
  itemSize: number
  /** What the layout puts between two of them. */
  gap: number
  /**
   * Where the axis begins inside the board's scrollable content, which is what a window
   * has to subtract from a scroll offset before it means anything.
   */
  start: number
  /**
   * How much of this axis the board shows at once: the width of its scroll viewport for
   * the columns, the depth of the band the rows are laid out in for the rows.
   *
   * The window compares this with the extent the axis needs, which it works out from the
   * item size and the count rather than from what is rendered. That is deliberate: a board
   * has to decide whether to window *before* it has drawn anything, because drawing the
   * whole board once to find out is the cost this exists to remove.
   */
  viewport: number
}

export type BoardMetrics = {
  columns: BoardAxisMetrics
  rows: BoardAxisMetrics
  isRtl: boolean
}

/**
 * The elements a measurement needs. Each is captured by one of the refs this hook hands
 * back, and a measurement taken before they are all attached is not taken at all.
 */
type BoardElements = {
  board: HTMLElement | null
  columnHeaders: HTMLElement | null
  rows: HTMLElement | null
  columnProbe: HTMLElement | null
  rowProbe: HTMLElement | null
}

export type BoardMetricsResult = {
  /** `null` until the board has been laid out once, and on the server, where it never is. */
  metrics: BoardMetrics | null
  columnHeadersRef: RefCallback<HTMLElement>
  rowsRef: RefCallback<HTMLElement>
  columnProbeRef: RefCallback<HTMLElement>
  rowProbeRef: RefCallback<HTMLElement>
}

export type UseBoardMetricsOptions = {
  /** The board's own scroll container, which is what both axes are measured against. */
  scrollRef: RefObject<HTMLElement | null>
  columnCount: number
  rowCount: number
}

/**
 * The board's two stated extents, resolved to pixels.
 *
 * A virtualizer counts in pixels and the board states its extents as CSS lengths: a
 * column is `min(var(--board-column-inline-size, 22rem), 100vw - var(--base-size-32))`
 * and a row is `var(--board-row-block-size, 100%)`, which the caller sets through
 * `rowHeight`. Only a browser can turn either into a number, so the board asks one:
 * `BoardMetricsProbes` renders an empty element carrying each frame's own class, and this
 * reads the box the stylesheet gave it.
 *
 * **Resolving a stated length is not measuring a row from its contents.** The probes hold
 * nothing, so there are no contents for their size to come from; what comes back is the
 * length the board already stated, in the units a window can use. The distinction is the
 * whole reason a row's height is a fact the caller states: a row measured from its cards
 * would leave the window estimating heights that only settle once each row has rendered,
 * and would leave a cell that never scrolls reporting a viewport as tall as everything in
 * it.
 *
 * Every number is re-read together, because they are read in one layout pass and keeping
 * halves of it from different frames is how a header ends up over the wrong cell. The
 * answer is compared with the last one and dropped when it has not moved, so a resize
 * that changes nothing does not re-render the board.
 */
export function useBoardMetrics({scrollRef, columnCount, rowCount}: UseBoardMetricsOptions): BoardMetricsResult {
  const [metrics, setMetrics] = useState<BoardMetrics | null>(null)

  const columnHeadersElementRef = useRef<HTMLElement | null>(null)
  const rowsElementRef = useRef<HTMLElement | null>(null)
  const columnProbeElementRef = useRef<HTMLElement | null>(null)
  const rowProbeElementRef = useRef<HTMLElement | null>(null)

  const remeasure = useCallback(() => {
    const next = measure({
      board: scrollRef.current,
      columnHeaders: columnHeadersElementRef.current,
      rows: rowsElementRef.current,
      columnProbe: columnProbeElementRef.current,
      rowProbe: rowProbeElementRef.current,
    })

    if (next === null) return

    setMetrics(previous => (previous !== null && isUnchanged(previous, next) ? previous : next))
  }, [scrollRef])

  // Every element that can move a number: the strip of rows, which shrinks when the
  // sticky header strip grows, and the two probes, which follow the stated lengths
  // themselves and so follow the viewport with them. One observer for all of them,
  // coalesced to a frame.
  const observe = useResizeObserver(remeasure)

  // The header strip is read rather than watched: its height reaches these numbers only
  // through the strip of rows, which is observed above and is the box that gives way.
  const columnHeadersRef = useCapture(columnHeadersElementRef)
  const rowsRef = useCapture(rowsElementRef, observe)
  const columnProbeRef = useCapture(columnProbeElementRef, observe)
  const rowProbeRef = useCapture(rowProbeElementRef, observe)

  // Before paint, so the board never shows a frame of every column it holds. The observer
  // above cannot do this job: it is coalesced to a frame, which is a frame too late.
  // Re-run on the counts because an axis that grew a page has more extent than the box it
  // sits in without any element having changed size, and nothing would report that.
  useLayoutEffect(() => {
    remeasure()
  }, [remeasure, columnCount, rowCount])

  return {metrics, columnHeadersRef, rowsRef, columnProbeRef, rowProbeRef}
}

/**
 * A ref that remembers its element, and optionally hands it to the shared observer.
 *
 * The cleanup form rather than waiting to be called with `null`: React 19 calls a
 * returned cleanup *instead* of that second call, so a ref that only assigned would keep
 * pointing at a detached element for the rest of the board's life.
 */
function useCapture(
  targetRef: RefObject<HTMLElement | null>,
  observe?: (node: Element | null) => (() => void) | undefined,
): RefCallback<HTMLElement> {
  return useCallback(
    node => {
      targetRef.current = node
      const stop = observe?.(node)

      return () => {
        targetRef.current = null
        stop?.()
      }
    },
    [observe, targetRef],
  )
}

function measure({board, columnHeaders, rows, columnProbe, rowProbe}: BoardElements): BoardMetrics | null {
  if (board === null || columnHeaders === null || rows === null || columnProbe === null || rowProbe === null) {
    return null
  }

  const boardRect = board.getBoundingClientRect()
  const columnHeadersRect = columnHeaders.getBoundingClientRect()
  const isRtl = getComputedStyle(board).direction === 'rtl'

  return {
    columns: {
      itemSize: columnProbe.getBoundingClientRect().width,
      gap: usedGap(columnHeaders, 'columnGap'),
      start: isRtl
        ? boardRect.left + board.clientLeft + board.clientWidth - columnHeadersRect.right - board.scrollLeft
        : columnHeadersRect.left - boardRect.left - board.clientLeft + board.scrollLeft,
      viewport: board.clientWidth,
    },
    rows: {
      itemSize: rowProbe.getBoundingClientRect().height,
      gap: usedGap(rows, 'rowGap'),
      start: rows.getBoundingClientRect().top - boardRect.top - board.clientTop + board.scrollTop,
      // The band the rows are laid out in rather than the whole board, because the sticky
      // header strip above it is not somewhere a row can be.
      viewport: rows.clientHeight,
    },
    isRtl,
  }
}

/** A resolved `gap`, which is `normal` on anything that never set one. */
function usedGap(element: HTMLElement, property: 'columnGap' | 'rowGap'): number {
  const gap = Number.parseFloat(getComputedStyle(element)[property])

  return Number.isFinite(gap) ? gap : 0
}

function isUnchanged(previous: BoardMetrics, next: BoardMetrics): boolean {
  return (
    previous.isRtl === next.isRtl &&
    isAxisUnchanged(previous.columns, next.columns) &&
    isAxisUnchanged(previous.rows, next.rows)
  )
}

function isAxisUnchanged(previous: BoardAxisMetrics, next: BoardAxisMetrics): boolean {
  return (
    previous.itemSize === next.itemSize &&
    previous.gap === next.gap &&
    previous.start === next.start &&
    previous.viewport === next.viewport
  )
}
